import { bannerError } from 'ember-simplepractice/utils/error-handling';
import { cached, tracked } from '@glimmer/tracking';
import { createFakeReservation } from 'client-portal/utils/prescreener/preview';
import { modifier } from 'ember-modifier';
import { waitForLater } from 'ember-simplepractice/utils/waiters';
import Service, { service } from '@ember/service';

const READY_FOR_PAYLOAD = 'preview-ready-for-payload';
const READY_TO_SHOW = 'preview-ready-to-show';

const RECEIVE_PREVIEW_PAYLOAD = 'preview-payload';

const CONNECTION_TIMEOUT = 30_000;
const GET_PAYLOAD_TICK = 300;

export default class PrescreenerPreviewService extends Service {
  @service currentPractice;
  @service prescreener;
  @service store;
  @service router;

  @tracked _previewConfig;

  _reservation;
  _isListening;
  _pendingPayload;
  _mode;

  _parentOrigin;

  set previewConfig(value) {
    if (typeof value === 'string') {
      try {
        this._previewConfig = JSON.parse(value);
      } catch (e) {
        this._previewConfig = null;
      }
    } else {
      this._previewConfig = value;
    }
  }

  get previewConfig() {
    return this._previewConfig;
  }

  @cached
  get isPreview() {
    return !!this._previewConfig;
  }

  postMessageModifier = modifier(async (_element, [mode]) => {
    if (this.isPreview && !this._isListening) {
      this._mode = mode;
      this.ensurePreviewReservation();

      const messageListener = this.onFrameMessage.bind(this);
      const cleanupMessageListener = this.setupMessageListener(messageListener);

      this._sendReadyForPayload();

      if (this.prescreener.hasNavCategories) {
        const prescreenerRoute = this.prescreener.step.getFirstPrescreenerRoute();
        this.router.transitionTo(prescreenerRoute);
      }

      return () => {
        cleanupMessageListener();
      };
    }
  });

  ensurePreviewReservation() {
    this._reservation ||= createFakeReservation(this.store, this.currentPractice.content);
  }

  setupMessageListener(handler, target = window) {
    this._isListening = true;
    target.addEventListener('message', handler);
    return () => {
      this._isListening = false;
      target.removeEventListener('message', handler);
    };
  }

  _sendReadyForPayload() {
    try {
      window.parent?.postMessage(this._readyForPayload, '*');
    } catch (e) {
      bannerError(e, 'Failed to send preview ready message');
    }
  }

  get _readyForPayload() {
    return JSON.stringify({
      status: READY_FOR_PAYLOAD,
      origin: window.location.origin,
    });
  }

  _sendReadyToShow() {
    try {
      window.parent?.postMessage(this._readyToShow, '*');
    } catch (e) {
      bannerError(e, 'Failed to send preview ready message');
    }
  }

  get _readyToShow() {
    return JSON.stringify({
      status: READY_TO_SHOW,
      mode: this._mode,
    });
  }

  onFrameMessage(e) {
    try {
      this.handlePayloadReceived(JSON.parse(e?.data || ''));
    } catch (_) {
      // no op
    }
  }

  handlePayloadReceived(message) {
    if (message?.action === RECEIVE_PREVIEW_PAYLOAD) {
      this._pendingPayload = message.payload;
      this._parentOrigin = message.origin;
      this.prescreener.updatePrescreenerModels();

      this._sendReadyToShow();
    }
  }

  async getPrescreenerPreview(timeoutMs = CONNECTION_TIMEOUT) {
    let deadline = Date.now() + timeoutMs;

    if (!this._isListening && this.isPreview) {
      this.ensurePreviewReservation();
      return this.store.createRecord('prescreener-question', this.previewConfig);
    }

    while (Date.now() < deadline) {
      if (this._pendingPayload) {
        return this.store.createRecord('prescreener-question', this._pendingPayload);
      }

      await waitForLater(GET_PAYLOAD_TICK);
    }

    throw new Error(`Preview did not respond within ${timeoutMs}ms`);
  }
}
